import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect } from "react";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";
import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import PrimaryButton from "../../../../../../common/components/primary_button";
import { ClienteArquivoForm, ClienteArquivoSchema } from "../../../schemas/Clientes_cadastro/clienteArquivoCadastro";
import { ClienteArquivo } from "../../../../domain/models/clienteArquivo";
import { DefaultFormDownloadInput } from "../../../../../../common/components/default_form_download_input";


/**
 * CadastroDocumentoModal
 */

type Props = {
    open: boolean,
    saving: boolean,
    selectedClienteArquivo: ClienteArquivo | null,
    onSubmit: (form: ClienteArquivoForm) => void,
    onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void,
    onDownload: (idClienteArquivo: number, nomeArquivo: string) => void,
    onDeleteFile: () => void,
    onClose: () => void,
};

export const ClienteArquivoModal = ({
    open,
    saving,
    selectedClienteArquivo,
    onDeleteFile,
    onSubmit,
    onOpenChangeHistory,
    onDownload,
    onClose,
}: Props) => {

    const hookForm = useForm<ClienteArquivoForm>({
        mode: "onSubmit",
        reValidateMode: "onSubmit",
        resolver: zodResolver(ClienteArquivoSchema),
    });

    useEffect(() => {
        if (open) {
            if (selectedClienteArquivo) {
                hookForm.setValue('idClienteArquivo', selectedClienteArquivo.idClienteArquivo);
                hookForm.setValue('descricao', selectedClienteArquivo.descricao);
                hookForm.setValue('nome', selectedClienteArquivo.nome);
                hookForm.setValue('limparArquivo', selectedClienteArquivo.limparArquivo ?? false);
            } else {
                hookForm.reset();
            }
        }
    }, [
        open,
        selectedClienteArquivo,
        hookForm,
    ]);

    const handleClose = useCallback(() => {
        if (!saving) onClose();
    }, [
        saving,
        onClose,
    ]);

    const handleDeleteFile = useCallback(() => {
        if (!saving) {
            if (window.confirm('Você deseja excluir este item?')) {
                onDeleteFile();
                hookForm.setValue('limparArquivo', true);
                hookForm.setValue('arquivo', null);
            }
        }
    }, [
        saving,
        hookForm,
        onDeleteFile,
    ]);


    const handleSubmit = useCallback((form: ClienteArquivoForm) => {
        if (!saving) onSubmit(form);
    }, [
        saving,
        onSubmit,
    ]);

    const handleChangeHistory = useCallback(() => {
        if (selectedClienteArquivo) {
            onOpenChangeHistory(selectedClienteArquivo.idClienteArquivo, LogTipoTabela.CLIENTE_ARQUIVO);
        }
    }, [selectedClienteArquivo, onOpenChangeHistory]);

    return (
        <Dialog
            open={open}
            slotProps={{
                paper: {
                    component: 'form',
                    onSubmit: hookForm.handleSubmit(handleSubmit),
                }
            }}
            sx={{
                "& .MuiDialog-container": {
                    "& .MuiPaper-root": {
                        width: "100%",
                        maxWidth: "35rem",
                    },
                },
            }}>
            <DialogTitle>
                Cadastrar documento
            </DialogTitle>

            <DialogContent>
                <ModalLoading loading={saving}>
                    <FormContainer title={"Cadastro Arquivo"} arrangement="fullRow">
                        <>

                            <DefaultFormInput
                                name={'descricao'}
                                label={"Nome do Arquivo"}
                                readOnly={saving}
                                required={true}
                                reactHookForm={hookForm}
                            />

                            <div className="flex w-full">
                                <DefaultFormDownloadInput
                                    name='arquivo'
                                    nameLimparFlag='limparArquivo'
                                    saving={saving}
                                    filename={hookForm.watch('nome') ?? null}
                                    readOnly={saving}
                                    maxFilenameSize={70}
                                    reactHookForm={hookForm}
                                    onDeleteFile={() => handleDeleteFile()}
                                    onDownloadFile={(_) => {
                                        if (selectedClienteArquivo && selectedClienteArquivo.nome) {
                                            onDownload(selectedClienteArquivo.idClienteArquivo, selectedClienteArquivo.nome);
                                        }
                                    }}
                                />
                            </div>

                        </>
                    </FormContainer>
                </ModalLoading>
            </DialogContent>

            <DialogActions>
                <div className="w-full h-full items-center justify-between flex p-4 gap-4">
                    <div>
                        {
                            isNotNullOrUndefined(selectedClienteArquivo) &&
                            <PrimaryButton
                                buttonColor="yellow"
                                onClick={handleChangeHistory}>
                                <span className="text-xs">
                                    Historico de alterações
                                </span>
                            </PrimaryButton>
                        }
                    </div>

                    <div className="flex flex-row gap-2">
                        <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
                            <h1
                                className="text-sm text-black dark:text-white"
                                onClick={handleClose}>
                                Cancelar
                            </h1>
                        </div>

                        <div className="w-[120px]">
                            {saving ? (
                                <PrimaryButton
                                    buttonColor="primary-green">
                                    Salvando...
                                </PrimaryButton>
                            ) : (
                                <PrimaryButton
                                    type="submit"
                                    buttonColor="primary-green">
                                    Salvar
                                </PrimaryButton>
                            )}
                        </div>
                    </div>
                </div>
            </DialogActions>
        </Dialog>
    );
}